import {
  Button,
  CheckboxField,
  Flex,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import capitalize from 'lodash-es/capitalize';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  ConditionType,
  EXCLUDE_TYPES,
  FORM_FIELD_DEFAULT_MARGIN,
} from '@jane/business-admin/components/shared/specials/modal/form';
import { useGlobalSpecialsModal } from '@jane/business-admin/providers/GlobalSpecialsModalProvider';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';
import { lineageOptions } from '@jane/shared-b2b/util';
import { allWeightIds, labelForWeightId } from '@jane/shared/util';

interface Props {
  conditions: string[];
  disableAddButton?: boolean;
  fieldPrefix: string;
  footer?: ReactNode;
  lastCondition?: boolean;
  onConditionsUpdate: (conditions: string[], type?: any) => void;
  type:
    | ConditionType.Lineage
    | ConditionType.Weight
    | ConditionType.ExcludeLineages
    | ConditionType.ExcludeWeights;
}

/**
 * Component for each condition line item in building conditions for a Special. This particular component can handle Lineage or Weight
 * @param props.conditions Simple list of conditions to display, values from a special should be mapped to match a simple conditions array
 * @param props.disableAddButton Disable the "Add Condition" and "Select (condition)" buttons, only allows for removing any existing conditions
 * @param props.fieldPrefix Used for input name fields, to prevent duplicates when there are two conditions cards in the case of BOGO specials
 * @param props.footer Rendered beneath the checkboxes, while the condition is expanded
 * @param props.onConditionsUpdate Callback when item is removed by clicking "X" icon, or user clears all by clicking "Remove condition"
 * @param props.type Type of condition
 * @param props.lastCondition Removes bottom border if true
 */
export const ConditionByCheckboxes = ({
  conditions: conditionsProp,
  disableAddButton,
  fieldPrefix,
  footer,
  onConditionsUpdate,
  type,
  lastCondition,
}: Props) => {
  const {
    storeSettings,
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { isDisabled: globalSpecialDisabled } = useGlobalSpecialsModal();
  const [expanded, setExpanded] = useState(!!conditionsProp.length);
  const [conditions, setConditions] = useState(conditionsProp);

  const includesOrExcludes =
    type.indexOf('exclude') >= 0 ? 'excludes' : 'includes';

  useEffect(() => {
    setExpanded(!!conditionsProp.length);
    setConditions(conditionsProp);
  }, [conditionsProp]);

  const weightOptions = useMemo(() => {
    const allWeights = allWeightIds('extract');
    return allWeights.map((weight: PriceId) => ({
      label: labelForWeightId(weight),
      value: weight,
    }));
  }, []);

  const options = useMemo(() => {
    if (
      type === ConditionType.Lineage ||
      type === ConditionType.ExcludeLineages
    ) {
      return lineageOptions(storeSettings?.custom_labels);
    } else {
      return weightOptions;
    }
  }, [type, storeSettings?.custom_labels, weightOptions]);

  useEffect(() => {
    setConditions(conditionsProp);
  }, [conditionsProp]);

  const removeCondition = () => {
    setConditions([]);
    setExpanded(false);

    onConditionsUpdate([]);
  };

  const onCheckboxChange = (checked: boolean, value: string) => {
    const filteredConditions = [...conditions];
    if (!checked) {
      filteredConditions.splice(filteredConditions.indexOf(value), 1);
    } else {
      filteredConditions.push(value);
    }

    setConditions(filteredConditions);
    onConditionsUpdate(filteredConditions);
  };

  const isAnyPosSynced = isJanePosSynced || posSynced;
  const isDisabled = isAnyPosSynced || isReadOnly || globalSpecialDisabled;

  const conditionButtonTestId = `${expanded ? 'remove' : 'add'}-${type.replace(
    / /g,
    '-'
  )}-condition`;

  return (
    <>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mb={FORM_FIELD_DEFAULT_MARGIN}
      >
        <Typography variant="body-semibold">{capitalize(type)}</Typography>
        <Flex gap={24}>
          <Button
            label={expanded ? 'Remove condition' : 'Add condition'}
            onClick={() => {
              expanded ? removeCondition() : setExpanded(true);
            }}
            variant={
              expanded
                ? 'tertiary'
                : EXCLUDE_TYPES.includes(type)
                  ? 'destructive-secondary'
                  : 'secondary'
            }
            disabled={(disableAddButton && !expanded) || isDisabled}
            data-testid={conditionButtonTestId}
          />
        </Flex>
      </Flex>
      {expanded && (
        <>
          <Flex gap={12} flexWrap="wrap">
            {options.map((option) => (
              <CheckboxField
                key={`key_${includesOrExcludes}_${option.value}`}
                mr={24}
                name={`${fieldPrefix}.${includesOrExcludes}.${option.value}`}
                label={option.label}
                onChange={(value) => onCheckboxChange(value, option.value)}
                checked={conditions.includes(option.value)}
                disabled={disableAddButton || isDisabled}
              />
            ))}
          </Flex>
          {footer}
          {!lastCondition && <Modal.ContentDivider />}
        </>
      )}
    </>
  );
};
